import type { AgentMessage } from "@earendil-works/pi-agent-core";

/**
 * Per-task localStorage cache of `user-with-attachments` messages so we can
 * restore PDF/image tiles after a reopen.
 *
 * Why this exists: when the user uploads a PDF in Cowork, PiTaskAgent locally
 * appends a `user-with-attachments` message (which pi-web-ui renders as a
 * tile). It also flattens the attachment to inline text + base64 images and
 * sends *that* over the WebSocket to the server. The server-side AgentSession
 * stores the flattened version in `agent.state.messages`, with no memory that
 * it was ever an attachment.
 *
 * On reconnect, the WS replays `agent.state.messages` as `message_replay`
 * events — but those are the flattened versions, so the user sees a wall of
 * extracted text instead of the original PDF tile.
 *
 * We keep a small client-side cache, indexed by the message's absolute
 * position in `state.messages` at save time, and the prompt text prefix as a
 * sanity-check fingerprint. On replay we swap the flattened message back out
 * for the cached attachment-shaped one when both match.
 */

const KEY = (taskId: string) => `task.${taskId}.attachments`;

export interface CachedAttachmentEntry {
	/** Position in agent.state.messages at the time of save. */
	index: number;
	/** First ~120 chars of the user's typed text — used to verify match. */
	textPrefix: string;
	/** The original `user-with-attachments` AgentMessage to restore on replay. */
	message: AgentMessage;
}

export function loadAttachmentCache(taskId: string): CachedAttachmentEntry[] {
	try {
		const raw = localStorage.getItem(KEY(taskId));
		if (!raw) return [];
		const parsed = JSON.parse(raw) as CachedAttachmentEntry[];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

export function saveAttachmentEntry(taskId: string, entry: CachedAttachmentEntry): void {
	try {
		const cur = loadAttachmentCache(taskId);
		// Replace any existing entry at the same index (defensive — shouldn't
		// happen in normal flow, but a HMR-reloaded session might re-prompt).
		const without = cur.filter((c) => c.index !== entry.index);
		without.push(entry);
		localStorage.setItem(KEY(taskId), JSON.stringify(without));
	} catch {
		/* quota / privacy mode — silently skip; user just loses the tile on reopen */
	}
}

export function clearAttachmentCache(taskId: string): void {
	try {
		localStorage.removeItem(KEY(taskId));
	} catch {
		/* ignore */
	}
}

/**
 * Pull a plain-text prefix from an AgentMessage's content for fingerprinting.
 * Handles both string content and content-block arrays.
 */
export function fingerprintText(message: AgentMessage, maxLen = 120): string {
	const content = (message as { content?: unknown }).content;
	let text = "";
	if (typeof content === "string") {
		text = content;
	} else if (Array.isArray(content)) {
		for (const c of content) {
			const t = (c as { type?: string }).type;
			if (t === "text") text += (c as { text?: string }).text ?? "";
		}
	}
	return text.trim().slice(0, maxLen);
}
